import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages the question popup dialog.
 * Manages its own dialog lifecycle (not extending DialogComponent)
 * to avoid arrow-function-property super.closeDialog() issues.
 *
 * @typedef {object} QuestionData
 * @property {string} title - The article title / question text
 * @property {string} content - The article body HTML
 * @property {string} imageUrl - The article featured image URL
 * @property {string} articleHandle - The article handle
 * @property {string} questionPreset - CSS class preset for question title
 * @property {string} answerPreset - CSS class preset for answer content
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element
 * @property {HTMLImageElement} popupImage - Background image
 * @property {HTMLElement} popupTitle - Title element
 * @property {HTMLElement} popupContent - Scrollable content area
 * @property {HTMLButtonElement} closeButton - Close button
 * @property {HTMLButtonElement} prevButton - Previous nav button
 * @property {HTMLButtonElement} nextButton - Next nav button
 * @property {HTMLElement} innerContainer - Outer card container (for click-outside detection)
 * @property {HTMLElement} contentPanel - Inner frosted glass content panel
 * @property {HTMLElement} imageWrapper - Image wrapper element
 * @property {HTMLUListElement} [grid] - The questions grid container
 * @property {HTMLButtonElement} [loadMoreButton] - Load more button
 * @property {HTMLElement[]} [cards] - The card elements
 *
 * @extends {Component}
 */
class QuestionPopupComponent extends Component {
  /** @type {QuestionData[]} */
  #questions = [];

  /** @type {number} */
  #currentIndex = 0;

  /** @type {HTMLElement|null} */
  #triggerElement = null;

  /** @type {number} */
  #previousScrollY = 0;

  /** @type {number} */
  #currentPage = 1;

  /** @type {AbortController|null} */
  #filterController = null;

  /** @type {number|null} */
  #scrollRafId = null;

  connectedCallback() {
    super.connectedCallback();
    this.#collectQuestions();

    const { grid } = this.refs;

    if (grid) {
      this.#currentPage = parseInt(grid.dataset.currentPage, 10) || 1;
    }

    this.#openFromUrlParam();
    this.#boundPopstate = this.#handlePopstate.bind(this);
    window.addEventListener('popstate', this.#boundPopstate);
    this.#initTagsScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.#boundPopstate);
    this.#filterController?.abort();

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
    }
  }

  /** @type {Function|null} */
  #boundPopstate = null;

  /**
   * Set up scroll fade indicators on the tags scroll container.
   */
  #initTagsScroll() {
    const { tagsScroll } = this.refs;
    if (!tagsScroll) return;

    tagsScroll.addEventListener('scroll', this.#updateScrollFade, { passive: true });
    this.#tagsResizeObserver.observe(tagsScroll);
    this.#updateScrollFade();
  }

  #updateScrollFade = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      const el = this.refs.tagsScroll;
      if (!el) return;

      const { scrollLeft, scrollWidth, clientWidth } = el;
      const maxScroll = scrollWidth - clientWidth;

      if (maxScroll <= 0) {
        el.style.setProperty('--tags-fade-start', '0');
        el.style.setProperty('--tags-fade-end', '0');
        return;
      }

      el.style.setProperty('--tags-fade-start', scrollLeft > 2 ? '1' : '0');
      el.style.setProperty('--tags-fade-end', scrollLeft < maxScroll - 2 ? '1' : '0');
    });
  };

  #tagsResizeObserver = new ResizeObserver(this.#updateScrollFade);

  /**
   * @returns {string}
   */
  get sectionId() {
    return this.getAttribute('section-id') || '';
  }

  /**
   * Scan all <template data-question-content> inside this component
   * and build the questions array.
   */
  #collectQuestions() {
    const templates = this.querySelectorAll('template[data-question-content]');
    this.#questions = Array.from(templates).map((template) => {
      const content = template.content.textContent || '';
      return JSON.parse(content);
    });
  }

  /**
   * Check for a ?question=handle URL parameter and auto-open that question's popup.
   * Loads all paginated pages if the question isn't on the current page.
   */
  async #openFromUrlParam() {
    const url = new URL(window.location.href);
    const rawHandle = url.searchParams.get('question');

    if (!rawHandle) return;

    // Strip any Shopify search tracking params that may leak into the value
    const questionHandle = rawHandle.split('?')[0];

    // Clean up the URL param immediately
    url.searchParams.delete('question');
    history.replaceState(null, '', url.toString());

    // Try to find the question in currently loaded questions
    let index = this.#findQuestionByHandle(questionHandle);

    // If not found, load all remaining pages then try again
    if (index === -1) {
      await this.#loadAllPages();
      index = this.#findQuestionByHandle(questionHandle);
    }

    if (index === -1) return;

    this.#currentIndex = index;
    this.#renderQuestion();
    this.#showDialog();
  }

  /**
   * Public method to open a question popup by article handle.
   * Used by the Is This OK block to open related articles.
   * @param {string} handle - The article handle
   * @returns {Promise<boolean>} Whether the popup was opened
   */
  async openByHandle(handle) {
    let index = this.#findQuestionByHandle(handle);

    if (index === -1) {
      await this.#loadAllPages();
      index = this.#findQuestionByHandle(handle);
    }

    if (index === -1) return false;

    this.#currentIndex = index;
    this.#renderQuestion();
    this.#showDialog();
    return true;
  }

  /**
   * Find a question's index by its article handle.
   * @param {string} handle - The article handle to search for
   * @returns {number} The index of the matching question, or -1 if not found
   */
  #findQuestionByHandle(handle) {
    return this.#questions.findIndex((q) => q.articleHandle === handle);
  }

  /**
   * Load all remaining paginated pages and append them to the grid.
   * Used when opening a question from a URL param that may be on a later page.
   */
  async #loadAllPages() {
    const { grid, loadMoreButton } = this.refs;

    if (!grid) return;

    const lastPage = parseInt(grid.dataset.lastPage, 10);

    while (this.#currentPage < lastPage) {
      const nextPage = this.#currentPage + 1;
      const url = new URL(window.location.href);
      url.searchParams.set('page', nextPage.toString());

      try {
        const html = await sectionRenderer.getSectionHTML(this.sectionId, false, url);
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = doc.querySelector('[ref="grid"]');

        if (newGrid) {
          const newItems = newGrid.querySelectorAll(':scope > [ref="cards[]"]');
          const existingCount = grid.querySelectorAll('[data-question-index]').length;

          for (const [i, item] of [...newItems].entries()) {
            const wrapper = item.querySelector('[data-question-index]');
            if (wrapper) {
              wrapper.setAttribute('data-question-index', existingCount + i);
            }
          }

          grid.append(...newItems);
        }

        this.#currentPage = nextPage;
        this.#collectQuestions();
      } catch (error) {
        console.error('Failed to load questions page:', error);
        break;
      }
    }

    // Hide load more button since all pages are now loaded
    if (loadMoreButton) {
      loadMoreButton.hidden = true;
    }
  }

  /**
   * Handle tag filter click — fetch filtered content via AJAX and update URL.
   * @param {MouseEvent} event
   */
  async handleTagClick(event) {
    event.preventDefault();

    const link = event.target.closest('a');
    if (!link) return;

    const targetUrl = link.href;

    history.pushState({ questionsFilter: true }, '', targetUrl);
    await this.#fetchFilteredSection(targetUrl);
  }

  /**
   * Handle browser back/forward to re-filter via AJAX.
   */
  async #handlePopstate() {
    await this.#fetchFilteredSection(window.location.href);
  }

  /**
   * Fetch the section HTML for a given URL and swap in the grid + tags.
   * @param {string} url - The full URL to fetch
   */
  async #fetchFilteredSection(url) {
    this.#filterController?.abort();
    this.#filterController = new AbortController();

    const { grid, loadMoreButton } = this.refs;
    if (grid) grid.setAttribute('aria-busy', 'true');

    try {
      const fetchUrl = new URL(url);
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false, fetchUrl);
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Swap tag nav active states
      const newTagsNav = doc.querySelector('[ref="tagsNav"]');
      if (newTagsNav && this.refs.tagsNav) {
        this.refs.tagsNav.innerHTML = newTagsNav.innerHTML;
      }

      // Swap grid content
      const newGrid = doc.querySelector('[ref="grid"]');
      if (newGrid && grid) {
        grid.innerHTML = newGrid.innerHTML;
        grid.dataset.lastPage = newGrid.dataset.lastPage || '1';
        grid.dataset.currentPage = newGrid.dataset.currentPage || '1';
        this.#currentPage = parseInt(grid.dataset.currentPage, 10) || 1;
      }

      // Swap load more button
      const newLoadMore = doc.querySelector('.questions-grid__load-more');
      const currentLoadMore = this.querySelector('.questions-grid__load-more');

      if (currentLoadMore && newLoadMore) {
        currentLoadMore.innerHTML = newLoadMore.innerHTML;
        currentLoadMore.hidden = false;
      } else if (currentLoadMore && !newLoadMore) {
        currentLoadMore.hidden = true;
      }

      this.#collectQuestions();
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Failed to filter questions:', error);
    } finally {
      if (grid) grid.removeAttribute('aria-busy');
    }
  }

  /**
   * Open the popup for a specific question card.
   * Called via on:click="/openQuestion" on card buttons.
   * @param {MouseEvent} event
   */
  openQuestion(event) {
    const card = event.target.closest('[data-question-index]');
    if (!card) return;

    const index = parseInt(card.getAttribute('data-question-index'), 10);
    if (isNaN(index) || index < 0 || index >= this.#questions.length) return;

    this.#triggerElement = card;
    this.#currentIndex = index;
    this.#renderQuestion();
    this.#showDialog();
  }

  /**
   * Load the next page of questions and append to the grid.
   * Called via on:click="/handleLoadMore" on the load more button.
   */
  async handleLoadMore() {
    const { grid, loadMoreButton } = this.refs;
    if (!grid || !loadMoreButton) return;

    const lastPage = parseInt(grid.dataset.lastPage, 10);
    const nextPage = this.#currentPage + 1;

    if (nextPage > lastPage) return;

    loadMoreButton.disabled = true;
    loadMoreButton.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('page', nextPage.toString());

      const html = await sectionRenderer.getSectionHTML(this.sectionId, false, url);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newGrid = doc.querySelector('[ref="grid"]');

      if (newGrid) {
        const newItems = newGrid.querySelectorAll(':scope > [ref="cards[]"]');

        for (const [i, item] of [...newItems].entries()) {
          item.classList.add('questions-grid__item--entering');
          item.style.animationDelay = `${i * 60}ms`;
          item.addEventListener('animationend', () => {
            item.classList.remove('questions-grid__item--entering');
            item.style.animationDelay = '';
          }, { once: true });
        }

        const existingCount = grid.querySelectorAll('[data-question-index]').length;

        for (const [i, item] of [...newItems].entries()) {
          const wrapper = item.querySelector('[data-question-index]');
          if (wrapper) {
            wrapper.setAttribute('data-question-index', existingCount + i);
          }
        }

        grid.append(...newItems);
      }

      this.#currentPage = nextPage;
      this.#collectQuestions();

      if (nextPage >= lastPage) {
        loadMoreButton.hidden = true;
      }
    } catch (error) {
      console.error('Failed to load more questions:', error);
    } finally {
      loadMoreButton.disabled = false;
      loadMoreButton.removeAttribute('aria-busy');
    }
  }

  /**
   * Show the dialog with scroll lock, focus trapping, and keyboard navigation.
   */
  #showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    this.#previousScrollY = window.scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.#previousScrollY}px`;

      dialog.show();

      // Show backdrop (sibling element outside dialog stacking context)
      if (this.refs.backdrop) {
        this.refs.backdrop.classList.add('question-popup__backdrop--active');
      }

      trapFocus(dialog);
      this.refs.closeButton?.focus();
    });

    this.addEventListener('keydown', this.#handleKeyDown);
    dialog.addEventListener('click', this.#handleBackdropClick);
  }

  /**
   * Close the dialog, restore scroll and focus.
   */
  closeDialog() {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    this.removeEventListener('keydown', this.#handleKeyDown);
    dialog.removeEventListener('click', this.#handleBackdropClick);
    removeTrapFocus();

    // Animate close
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    onAnimationEnd(dialog, undefined, { subtree: false }).then(() => {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

      dialog.close();
      dialog.classList.remove('dialog-closing');
      if (this.refs.backdrop) {
        this.refs.backdrop.classList.remove('question-popup__backdrop--active');
      }

      if (this.#triggerElement) {
        const button = this.#triggerElement.querySelector('button') || this.#triggerElement;
        button.focus();
        this.#triggerElement = null;
      }
    });
  }

  /**
   * Update popup content with the current question's data.
   * @param {boolean} [animate=false] - Whether to cross-fade the content
   */
  #renderQuestion(animate = false) {
    const question = this.#questions[this.#currentIndex];
    if (!question) return;

    const hasMultiple = this.#questions.length > 1;
    this.refs.prevButton.disabled = !hasMultiple;
    this.refs.nextButton.disabled = !hasMultiple;

    if (!animate) {
      this.#applyQuestionContent(question);
      this.#preloadAdjacentImages();
      return;
    }

    const panel = this.refs.contentPanel;
    const imageWrapper = this.refs.imageWrapper;

    // Fade out old content
    panel.classList.add('question-popup__inner--fading');
    if (imageWrapper) imageWrapper.classList.add('question-popup__image-wrapper--fading');

    // Wait for fade-out to complete, swap content, then fade in
    panel.addEventListener('transitionend', () => {
      this.#applyQuestionContent(question);

      requestAnimationFrame(() => {
        panel.classList.remove('question-popup__inner--fading');
        if (imageWrapper) imageWrapper.classList.remove('question-popup__image-wrapper--fading');
        this.#preloadAdjacentImages();
      });
    }, { once: true });
  }

  /**
   * Apply question data to the popup elements.
   * @param {QuestionData} question
   */
  #applyQuestionContent(question) {
    const title = this.refs.popupTitle;
    const content = this.refs.popupContent;
    const panel = this.refs.contentPanel;
    const img = this.refs.popupImage;

    title.textContent = question.title;
    content.innerHTML = question.content;
    content.scrollTop = 0;

    // Use same 700px URL as the card so the browser cache is shared
    img.src = question.imageUrl;
    img.srcset = '';
    img.alt = '';

    // Apply text presets as CSS classes
    title.className = 'question-popup__title';
    if (question.questionPreset) {
      title.classList.add(question.questionPreset);
    }

    content.className = 'question-popup__content';
    if (question.answerPreset) {
      content.classList.add(question.answerPreset);
    }

    // Detect overflow to toggle vertical centering vs top-alignment
    requestAnimationFrame(() => {
      const hasOverflow = panel.scrollHeight > panel.clientHeight;
      panel.classList.toggle('question-popup__inner--has-overflow', hasOverflow);
    });
  }

  /**
   * Preload images for previous and next questions so they're ready on navigation.
   */
  #preloadAdjacentImages() {
    if (this.#questions.length <= 1) return;

    const prevIndex = (this.#currentIndex - 1 + this.#questions.length) % this.#questions.length;
    const nextIndex = (this.#currentIndex + 1) % this.#questions.length;

    for (const idx of [prevIndex, nextIndex]) {
      const url = this.#questions[idx]?.imageUrl;
      if (url) {
        const img = new Image();
        img.src = url;
      }
    }
  }

  /**
   * Navigate to the previous question (wraps around).
   */
  navigatePrev() {
    this.#currentIndex = (this.#currentIndex - 1 + this.#questions.length) % this.#questions.length;
    this.#renderQuestion(true);
  }

  /**
   * Navigate to the next question (wraps around).
   */
  navigateNext() {
    this.#currentIndex = (this.#currentIndex + 1) % this.#questions.length;
    this.#renderQuestion(true);
  }

  /**
   * Handle keyboard events — Escape to close, arrows to navigate.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDialog();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.navigatePrev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigateNext();
    }
  };

  /**
   * Close when clicking outside the inner container or nav buttons.
   * @param {MouseEvent} event
   */
  #handleBackdropClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const inner = this.refs.innerContainer;
    if (inner && inner.contains(event.target)) return;

    const isNavButton = event.target.closest('.question-popup__nav-button');
    if (isNavButton) return;

    this.closeDialog();
  };
}

if (!customElements.get('question-popup-component')) {
  customElements.define('question-popup-component', QuestionPopupComponent);
}
